/**
 * Link expired (410) · Not found (404) · No access (403) — screenshots/106–108, brief §2.20. One line of copy and one
 * next step each. Same layout as sign-up: expired = the strap with no pass; 404 = a pass for "Nowhere, Chicago";
 * no access = the viewer's pass with a NOT VALID stamp. The Worker serves /d/:token misses with 410 and an
 * inlined {kind:"expired"} (snapshot:system) so first paint is correct.
 */
import { useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { passView, type PassView } from "../auth/civicPass";
import { LanyardBadge } from "../auth/LanyardBadge";

export type SystemKind = "expired" | "notFound" | "noAccess";

const COPY: Record<SystemKind, { title: string; sub: (x: { name?: string; team?: string }) => string; primary: [string, string]; secondary?: [string, string] }> = {
  expired: { title: "This link expired.", sub: () => "Ask whoever shared it for a new one.", primary: ["Sign in", "/signin"], secondary: ["Get a Civic Pass", "/signup"] },
  notFound: { title: "Page not found.", sub: () => "It may have moved. Try search.", primary: ["Go to my Overview", "/dashboard"], secondary: ["Search ⌘K", "/dashboard/search"] },
  noAccess: { title: "No access.", sub: ({ name, team }) => (name && team ? `“${name}” is shared with the ${team} only.` : "This is shared with a team you’re not on."), primary: ["Request access", "#request"], secondary: ["Back to Analytics", "/dashboard/analytics"] },
};

const NOWHERE: PassView = { ...passView({ name: "Nowhere, Chicago", email: "", emailVerified: false, group: null, topics: [], follows: [] }),
  role: "Page not found", fields: [{ label: "Ward", value: "51 (no such ward)" }, { label: "Alderperson", value: "—" }, { label: "Following", value: null }, { label: "Email", value: null }],
  status: "NOT ISSUED", number: "Nº 0000404", initials: "?" };

export function SystemPage({ kind, myPass, resource, onRequestAccess, onSearch }: {
  kind: SystemKind;
  myPass?: PassView | null;                          // signed-in viewer's pass (no access)
  resource?: { name: string; team: string };
  onRequestAccess?: () => void;
  /** Signed in: "Search ⌘K" opens the ⌘K palette instead of navigating. */
  onSearch?: () => void;
}) {
  const c = COPY[kind];
  const view = kind === "noAccess" && myPass ? myPass : NOWHERE;
  useEffect(() => { document.title = `${c.title.replace(/\.$/, "")} · Chicago Politics`; }, [c.title]);
  return (
    <div className="auth system">
      <main className="auth__form">
        <header className="auth__top">
          <a href="/"><img src="/brand/chicago-politics.png" alt="Chicago Politics" width={150} height={60} /></a>
          <span>Need help? <a className="link" href="mailto:hello@chicagopolitics.org">Contact us</a></span>
        </header>
        <section className="auth__body">
          <h1 className="auth__title">{c.title}</h1>
          <p className="auth__sub">{c.sub(resource ?? {})}</p>
          <div className="auth__row">
            {kind === "noAccess" && onRequestAccess
              ? <button className="auth__primary" onClick={onRequestAccess}>{c.primary[0]}<ArrowRight size={18} aria-hidden /></button>
              : <a className="auth__primary" href={c.primary[1]}>{c.primary[0]}<ArrowRight size={18} aria-hidden /></a>}
            {c.secondary && (kind === "notFound" && onSearch
              ? <button className="auth__secondary" onClick={onSearch}>{c.secondary[0]}</button>
              : <a className="auth__secondary" href={c.secondary[1]}>{c.secondary[0]}</a>)}
          </div>
        </section>
      </main>
      <aside className="auth__stage" aria-hidden>
        <LanyardBadge view={view} bare={kind === "expired"} stamp={kind === "noAccess" ? "NOT VALID FOR THIS DASHBOARD" : undefined} />
      </aside>
    </div>
  );
}
